'use client'

import { useCallback, useState } from 'react'
import { Banner, BlockStack, Button, InlineStack, TextField } from '@shopify/polaris'
import { SUPPORT_EMAIL } from '@/lib/contact'

type FieldKey = 'firstName' | 'lastName' | 'email' | 'message'

type FieldErrors = Partial<Record<FieldKey, string>>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validateField(key: FieldKey, value: string): string | undefined {
  const t = value.trim()
  switch (key) {
    case 'firstName':
      if (t.length < 1 || t.length > 80) return 'Enter your first name (1–80 characters).'
      return undefined
    case 'lastName':
      if (t.length < 1 || t.length > 80) return 'Enter your last name (1–80 characters).'
      return undefined
    case 'email':
      if (t.length < 5 || t.length > 254 || !EMAIL_RE.test(t)) return 'Enter a valid email address.'
      return undefined
    case 'message':
      if (t.length < 10 || t.length > 8000) return 'Enter a message (10–8000 characters).'
      return undefined
    default:
      return undefined
  }
}

function validateAll(values: Record<FieldKey, string>): FieldErrors {
  const out: FieldErrors = {}
  ;(['firstName', 'lastName', 'email', 'message'] as const).forEach((k) => {
    const err = validateField(k, values[k])
    if (err) out[k] = err
  })
  return out
}

export function ContactForm() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [company, setCompany] = useState('')

  const [touched, setTouched] = useState<Partial<Record<FieldKey, true>>>({})
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [banner, setBanner] = useState<{ tone: 'success' | 'critical'; title: string; message?: string } | null>(null)

  const values: Record<FieldKey, string> = { firstName, lastName, email, message }

  const blur = useCallback(
    (key: FieldKey) => {
      setTouched((prev) => ({ ...prev, [key]: true }))
      const err = validateField(key, values[key])
      setFieldErrors((prev) => ({ ...prev, [key]: err }))
    },
    [values]
  )

  const onChangeFirst = useCallback(
    (v: string) => {
      setFirstName(v)
      if (touched.firstName) setFieldErrors((prev) => ({ ...prev, firstName: validateField('firstName', v) }))
    },
    [touched.firstName]
  )
  const onChangeLast = useCallback(
    (v: string) => {
      setLastName(v)
      if (touched.lastName) setFieldErrors((prev) => ({ ...prev, lastName: validateField('lastName', v) }))
    },
    [touched.lastName]
  )
  const onChangeEmail = useCallback(
    (v: string) => {
      setEmail(v)
      if (touched.email) setFieldErrors((prev) => ({ ...prev, email: validateField('email', v) }))
    },
    [touched.email]
  )
  const onChangeMessage = useCallback(
    (v: string) => {
      setMessage(v)
      if (touched.message) setFieldErrors((prev) => ({ ...prev, message: validateField('message', v) }))
    },
    [touched.message]
  )

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setBanner(null)
      setTouched({ firstName: true, lastName: true, email: true, message: true })
      const errs = validateAll(values)
      setFieldErrors(errs)
      if (Object.keys(errs).length > 0) return

      setSubmitting(true)
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            message: message.trim(),
            source: 'app',
            company,
          }),
        })
        const data = (await res.json()) as {
          errors?: FieldErrors
          error?: string
          mailtoFallback?: string
        }

        if (res.status === 400 && data.errors) {
          setFieldErrors(data.errors)
          return
        }

        if (!res.ok) {
          setBanner({
            tone: 'critical',
            title: 'Could not send',
            message:
              data.error ||
              (typeof data.mailtoFallback === 'string'
                ? `Email us at ${SUPPORT_EMAIL} or use your mail app.`
                : undefined),
          })
          return
        }

        setBanner({
          tone: 'success',
          title: 'Message sent',
          message: "Thanks — we've emailed you a confirmation and will reply soon.",
        })
        setFirstName('')
        setLastName('')
        setEmail('')
        setMessage('')
        setCompany('')
        setTouched({})
        setFieldErrors({})
      } catch {
        setBanner({
          tone: 'critical',
          title: 'Network error',
          message: `Try again or email ${SUPPORT_EMAIL}.`,
        })
      } finally {
        setSubmitting(false)
      }
    },
    [firstName, lastName, email, message, company, values]
  )

  return (
    <form onSubmit={onSubmit}>
      <BlockStack gap="400">
        {banner ? (
          <Banner tone={banner.tone} title={banner.title} onDismiss={() => setBanner(null)}>
            {banner.message ? <p>{banner.message}</p> : null}
          </Banner>
        ) : null}

        <div className="sigma-contact-honeypot" aria-hidden="true">
          <label htmlFor="contact-company">Company</label>
          <input
            id="contact-company"
            name="company"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(ev) => setCompany(ev.target.value)}
          />
        </div>

        <InlineStack gap="300" wrap={false} blockAlign="start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="First name"
              name="firstName"
              value={firstName}
              onChange={onChangeFirst}
              onBlur={() => blur('firstName')}
              autoComplete="given-name"
              error={fieldErrors.firstName}
              requiredIndicator
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="Last name"
              name="lastName"
              value={lastName}
              onChange={onChangeLast}
              onBlur={() => blur('lastName')}
              autoComplete="family-name"
              error={fieldErrors.lastName}
              requiredIndicator
            />
          </div>
        </InlineStack>

        <TextField
          label="Email"
          name="email"
          type="email"
          value={email}
          onChange={onChangeEmail}
          onBlur={() => blur('email')}
          autoComplete="email"
          error={fieldErrors.email}
          requiredIndicator
        />

        <TextField
          label="Message"
          name="message"
          value={message}
          onChange={onChangeMessage}
          onBlur={() => blur('message')}
          multiline={5}
          autoComplete="off"
          helpText="Include your store name if you need help with SigmaSense in Shopify."
          error={fieldErrors.message}
          requiredIndicator
        />

        <Button variant="primary" submit loading={submitting}>
          Send message
        </Button>
      </BlockStack>
    </form>
  )
}
